import { invariant } from '@mimir/common/util/invariant';
import { makeError } from '@mimir/common/util/exception';
import type { PromiseSource } from '@mimir/common/async/async';
import { defer, neverCancel } from '@mimir/common/async/async';
import { makePriority, PrioritySemaphore } from '@mimir/common/async/prioritySemaphore';

/**
 * Coordinate communication with media parser buffers, according to the
 * following rules:
 *
 * 1. Media parser buffers can only process one update at a time, so we'll need
 *    to queue actions that add segments to the buffer.
 *
 * 2. Decoders need to be initialized before decoding media, so we need to make
 *    sure to add appropriate initialization segments before adding media
 *    segments.
 *
 * 3. Media parser buffers will fill up and disallow adding more media, so we
 *    must make sure to evict old media when that happens.
 */
export class BufferCoordinator {
  /**
   * Semaphore to limit actions that make use of buffer updates to one at a
   * time for a given buffer.
   */
  oneUpdateAtATime: PrioritySemaphore;

  /**
   * The initialization segment the decoder is current initialized for.
   */
  currentInitialization: {
    key: string;
    buffer: BufferSource;
  } | null = null;

  lastQueued: string | null = null;

  hasEnded = false;
  lastUpdateFailed: boolean = false;

  ready: PromiseSource<void> | null = null;

  readonly checkReady: () => void;

  constructor(readonly buffer: SourceBuffer) {
    this.oneUpdateAtATime = new PrioritySemaphore(1);
    this.checkReady = () => {
      if (this.buffer.updating || this.ready == null) {
        return;
      }

      const deferred = this.ready.deferred;
      this.ready = null;
      deferred.resolve();
    };

    /*
      Different browser implementations have different quirks regarding when
      these events are triggered. An event does not necessarily correlate with
      an ongoing buffer update action. Therefore we only use these events as
      hints to recheck the buffer "updating" state, rather than a definitive
      indication that ongoing update operations have concluded.
    */
    for (const type of ['abort', 'update', 'updateend']) {
      this.buffer.addEventListener(type, this.checkReady);
    }

    this.buffer.addEventListener('error', () => {
      this.lastUpdateFailed = true;
      this.checkReady();
    });

    /*
      We're not listening to the 'updateend' event, since it appears to be fired
      even when no update action has been triggered, and can therefore not be
      relied upon for anything.
    */
  }

  waitForReadyNotification(): Promise<void> {
    invariant(this.buffer.updating, 'Expected updating');
    if (this.ready == null) {
      this.ready = defer<void>();
    }

    return this.ready.promise;
  }

  async waitForReady(): Promise<void> {
    while (this.buffer.updating) {
      await this.waitForReadyNotification();
    }
  }

  /**
   * Try once to run an action that will start an update operation, returns a promise that
   * tracks the completion of the update operation.
   */
  private async runUpdate(info: Record<string, any>, startAction: () => void): Promise<void> {
    await this.waitForReady();
    invariant(!this.buffer.updating, 'Did not expect updating');
    this.lastUpdateFailed = false;
    startAction();
    if (!this.buffer.updating) {
      console.warn('No updating triggered by action', info);
    }

    await this.waitForReady();
    if (this.lastUpdateFailed) {
      throw makeError('Buffer update failed', 'UpdateError', { info });
    }
  }

  /**
   * Add a media or initialization segment if there is room in the decoder
   * buffer to do so.
   */
  private async tryAddSegment(segment: BufferSource, info: Record<string, any>): Promise<boolean> {
    try {
      await this.runUpdate({ ...info, op: 'appendBuffer' }, () => {
        this.buffer.appendBuffer(segment);
      });
      return true;
    } catch (ex) {
      if (ex != null) {
        const exa = ex as { name?: unknown };
        if (exa.name != 'QuotaExceededError') {
          throw ex;
        }
      }
    }
    return false;
  }

  /**
   * Try to make more room in the decoder buffer by removing media with
   * timestamps before the current time. If we never evict media, then the
   * decoder buffer will eventually reach its quota, and we will be unable to
   * add more media to the buffer.
   */
  private async evictMedia(start: number, end: number) {
    await this.runUpdate({ start, end, op: 'remove' }, () => {
      if (end > start) {
        try {
          this.buffer.remove(start, end);
        } catch(ex) {
          throw makeError('Could not make room in decoder buffer', 'EvictError', { cause: ex, info: { start, end }});
        }
      }
    });

    /*
      Invalidate the hasEnded flag, since the evicted media may include the last
      segment.
    */
    this.hasEnded = false;
  }

  /**
   * Try to add an initialization or media segment to the decoder buffer, using
   * an evition strategy if the decoder buffer is already full. The eviction
   * strategy avoid evicting media from a hot interval which is typically where
   * playback currently is occuring.
   */
  private async tryBufferSegment(segment: BufferSource, info: Record<string, any>, hotStart: number, hotEnd: number): Promise<boolean> {
    if (await this.tryAddSegment(segment, info)) {
      return true;
    }

    // First try to evict before the hot interval.
    await this.evictMedia(0, hotStart);

    if (await this.tryAddSegment(segment, info)) {
      return true;
    }

    // Then try to evict after the hot interval.
    const bufferedLength = this.buffer.buffered.length;
    if (bufferedLength == 0) {
      return false;
    }

    const bufferedEnd = this.buffer.buffered.end(bufferedLength - 1);
    await this.evictMedia(hotEnd, bufferedEnd);

    if (await this.tryAddSegment(segment, info)) {
      return true;
    }

    /*
      We're unable to add the buffer even after trying to remove media before
      the current time.

      It is tempting to use this as an indication of how much data the decode
      buffer can hold, and adjust our future fetching rate thereafter. However
      due to the asynchronous nature of decoding and updates to current time,
      this will at times be misleading, and therefore cannot be trusted.
    */
    return false;
  }

  evictAll() {
    const bufferedLength = this.buffer.buffered.length;
    if (bufferedLength == 0) {
      return;
    }
    const bufferedEnd = this.buffer.buffered.end(bufferedLength - 1);
    return this.evictMedia(0, bufferedEnd);
  }

  /**
   * Add a media segment to the decoder buffer. This action may be delayed if
   * the buffer is not initialized yet, or other media segments are already in
   * the process of being appended.
   *
   * Media segments can only be properly decoded if the decoder has been
   * initialized appropriately first with an initialization segment. We use a
   * string key to identify unique initialization segments, so that we can skip
   * reinitializing the decoder if the initialization segment required remains
   * the same.
   */
  async bufferMediaSegment(initKey: string, initializationSegment: BufferSource, mediaSegment: BufferSource, isFinalSegment: boolean, info: Record<string, any>, hotStart: number, hotEnd: number, taskId: string): Promise<void> {
    this.lastQueued = taskId;
    await this.oneUpdateAtATime.limit(async () => {
      if (initKey != this.currentInitialization?.key) {
        if (!await this.tryBufferSegment(initializationSegment, { ...info, initKey }, hotStart, hotEnd)) {
          return;
        }

        this.currentInitialization = {
          key: initKey,
          buffer: initializationSegment
        };
      }

      if (await this.tryBufferSegment(mediaSegment, info, hotStart, hotEnd)) {
        this.hasEnded = isFinalSegment;
      }
    }, makePriority(), neverCancel);
  }
}

